export const PENDING_CONFIG_PROVISIONING_KEY = "@easyroom:config-provisioning"

const PENDING_PROVISIONING_MAX_AGE_MS = 24 * 60 * 60 * 1000

export interface PendingConfigProvisioning {
    created_at: number
    hotel_ids: string[]
    state: string
}

export interface ProvisioningProfile {
    roles?: Array<{
        eliminado?: boolean | null
        nombre?: string | null
    }> | null
    suscripciones?: Array<{
        suscripcion_id?: string | null
        eliminado?: boolean | null
        estatus?: string | null
    }> | null
    hotel?: Array<{
        hotel_id?: string | null
        suscripciones?: Array<{
            suscripcion_id?: string | null
            estatus?: string | null
            eliminado?: boolean | null
            modulos?: Array<{
                nombre_modulo?: string | null
            }> | null
        }> | null
    }> | null
}

export const storePendingConfigProvisioning = (hotelIds: string[], state: string, now = Date.now()): void => {
    const record: PendingConfigProvisioning = {
        created_at: now,
        hotel_ids: Array.from(new Set(hotelIds.filter(Boolean))),
        state,
    }
    sessionStorage.setItem(PENDING_CONFIG_PROVISIONING_KEY, JSON.stringify(record))
}

export const readPendingConfigProvisioning = (now = Date.now()): PendingConfigProvisioning | null => {
    const raw = sessionStorage.getItem(PENDING_CONFIG_PROVISIONING_KEY)
    if (!raw) return null

    try {
        const record = JSON.parse(raw) as PendingConfigProvisioning
        const valid =
            Number.isFinite(record.created_at) &&
            record.created_at > 0 &&
            now - record.created_at <= PENDING_PROVISIONING_MAX_AGE_MS &&
            Array.isArray(record.hotel_ids) &&
            record.hotel_ids.every((id) => typeof id === "string") &&
            typeof record.state === "string" &&
            /^[0-9a-f-]{36}$/i.test(record.state)

        if (valid) return record
    } catch {
        // Invalid or stale records must not influence hotel selection.
    }

    sessionStorage.removeItem(PENDING_CONFIG_PROVISIONING_KEY)
    return null
}

export const clearPendingConfigProvisioning = (): void => {
    sessionStorage.removeItem(PENDING_CONFIG_PROVISIONING_KEY)
}

export const selectProvisionedHotelId = (
    profile: ProvisioningProfile | null | undefined,
    snapshotHotelIds?: readonly string[]
): string | null => {
    const activeUserSubscriptionIds = new Set(
        (profile?.suscripciones ?? [])
            .filter((subscription) => !subscription.eliminado && subscription.estatus === "Activa")
            .map((subscription) => subscription.suscripcion_id)
            .filter((subscriptionId): subscriptionId is string => !!subscriptionId)
    )
    const hasActiveRole = profile?.roles?.some((role) => !role.eliminado && !!role.nombre)
    if (!activeUserSubscriptionIds.size || !hasActiveRole) return null

    const activeHotelIds = Array.from(
        new Set(
            (profile?.hotel ?? [])
                .filter((hotel) =>
                    hotel.suscripciones?.some(
                        (suscripcion) =>
                            !suscripcion.eliminado &&
                            suscripcion.estatus === "Activa" &&
                            !!suscripcion.suscripcion_id &&
                            activeUserSubscriptionIds.has(suscripcion.suscripcion_id) &&
                            !!suscripcion.modulos?.length
                    )
                )
                .map((hotel) => hotel.hotel_id)
                .filter((hotelId): hotelId is string => !!hotelId)
        )
    )

    if (snapshotHotelIds) {
        const snapshot = new Set(snapshotHotelIds)
        const newHotelIds = activeHotelIds.filter((hotelId) => !snapshot.has(hotelId))
        return newHotelIds.length === 1 ? newHotelIds[0] : null
    }

    return activeHotelIds.length === 1 ? activeHotelIds[0] : null
}
